import {useParams} from "react-router-dom";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {getStationData, getAllData, getTrend, getInterval, getNow} from "../../services/api";
import {WeatherDataContext} from "../../contexts/WeatherDataContextProvider";
import {FEATURED_TAKE_OFF_SITE, TAKE_OFF_SITES, ALL_SITES} from "../../constants";
import {WindRoseChart} from "../../components/WindRoseChart";
import Box from "../../components/Box";
import { StationDashboardSkeleton } from "../../components/Skeleton";
import {useInterval} from "../../hooks/useInterval";
// Old box components removed - using WeatherDataBox instead
import TakeoffPositionMap from "../../components/TakeoffPositionMap";
import WindBarbChart from "../../components/WindBarbChart";
import Highcharts from "highcharts/highstock";
import {DarkUnicaOptions} from "../../utils/DarkUnicaOptions";
import HcMore from "highcharts/highcharts-more";
import {isDesktop, isMobile, isTablet, useMobileOrientation} from "react-device-detect";
import {useTranslation} from "react-i18next";
import StationDashboardHeader from "../../components/StationDashboardHeader";
import MobileTabLayout from "../../components/MobileTabLayout";
import WeatherDataBox from "../../components/WeatherDataBox";
import WindBoxV2 from "../../components/WindBoxV2";
import { FiThermometer, FiDroplet, FiWind, FiCloud, FiSunrise, FiAlertTriangle, FiWifiOff, FiBarChart2 } from "react-icons/fi";
import { WiBarometer, WiDaySunnyOvercast } from "react-icons/wi";
import dayjs from "dayjs";
import {Helmet} from "react-helmet";
import IonRangeSlider from "react-ion-slider";
import {useForecastContext} from "../../contexts/ForecastContextProvider";
import {MdOutlineArrowBackIosNew, MdOutlineArrowForwardIos} from "react-icons/md";
import {FaLocationArrow} from "react-icons/fa";
import PointTable from "../../components/PointTableV2";
import {TbPointerPin} from "react-icons/tb";
import { getFlightSafetyStatus } from '../../utils/flightSafety';
import { useSetDashboardActions } from '../../contexts/DashboardActionsContext';

HcMore(Highcharts);
Highcharts.setOptions(DarkUnicaOptions());
const StationDashboard = () => {
    const {t} = useTranslation();
    const {isLandscape} = useMobileOrientation()

    const {takeoffSiteSlug} = useParams();
    const [takeOffSite, setTakeOffSite] = useState()
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    const {availableTime, date, time, setDate, setTime, setPosition, mapVisible, setMapVisible} = useForecastContext()
    const {
        stationData,
        setStationData,
        allData,
        setAllData,
        interval15,
        setInterval15,
        interval30,
        setInterval30,
        interval60,
        setInterval60,
        trend,
        setTrend,
        now,
        setNow,
    } = useContext(WeatherDataContext)
    const navigate = useNavigate();
    const goToPointForecast = () => {
        if (!stationData) return;
        const params = new URLSearchParams({
            lat: Number(stationData.LAT).toFixed(5),
            lng: Number(stationData.LONG).toFixed(5),
            feature: 'point',
        });
        if (takeoffSiteSlug) params.set('site', takeoffSiteSlug);
        navigate(`/forecast?${params.toString()}`);
    };
    const getData = async (isRefresh = false) => {
        // Show the spinning refresh icon for any in-flight data update —
        // user-triggered refresh, initial load (after the skeleton), and the
        // auto-refresh interval — not just manual refresh.
        setRefreshing(true);
        try {
            const [stationData, allData, now, interval15, interval30, interval60, trend] = await Promise.all([getStationData(takeOffSite.id), getAllData(takeOffSite.id), getNow(takeOffSite.id), getInterval(takeOffSite.id, 15), getInterval(takeOffSite.id, 30), getInterval(takeOffSite.id, 60), getTrend(takeOffSite.id)]);
            setStationData(stationData)
            setAllData(allData)
            setNow(now)
            setInterval15(interval15)
            setInterval30(interval30)
            setInterval60(interval60)
            setTrend(trend)
            setPosition({lat: stationData.LAT, lng: stationData.LONG})
            setLastUpdate(new Date());
        } catch (error) {
            console.error('Failed to fetch data:', error);
            if (!isRefresh) window.location.reload();
        } finally {
            setRefreshing(false);
        }
    }
    useEffect(() => {
        // Reset all per-station state so the previous site's map/cards/charts
        // unmount immediately. Without this, navigating between sites would
        // keep the previous data visible until the new fetch completes — and
        // the Leaflet map wouldn't recenter at all (it only honors `center`
        // on first mount).
        setStationData(null);
        setAllData(null);
        setNow(null);
        setInterval15(null);
        setInterval30(null);
        setInterval60(null);
        setTrend(null);
        setLoading(true);

        const takeOffSiteLocal = ALL_SITES.find(site => site.slug === takeoffSiteSlug) ||
                                (FEATURED_TAKE_OFF_SITE?.slug === takeoffSiteSlug ? FEATURED_TAKE_OFF_SITE : null);
        setTakeOffSite(takeOffSiteLocal);
        if (!takeOffSiteLocal) {
            navigate('/')
        }
    }, [takeoffSiteSlug]);

    useEffect(() => {
        if (takeOffSite) {
            (async () => {
                await getData();
                setLoading(false)
            })()
        }
    }, [takeOffSite])

    useInterval(async () => {
        if (takeOffSite) {
            setRefreshing(true);
            try {
                const now = await getNow(takeOffSite.id);
                setNow(now);
                setLastUpdate(new Date());
            } finally {
                setRefreshing(false);
            }
        }
    }, 60000)

    // When the user returns to the tab after being away, the cached `now`
    // value is stale and getFlightSafetyStatus marks the station offline
    // (SEND_TIME > 5 min old). Immediately refetch on visibility change.
    useEffect(() => {
        const onVisibility = () => {
            if (document.visibilityState === 'visible' && takeOffSite) {
                getData(true);
            }
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [takeOffSite]);


    const handleShare = async () => {
        // Guard: don't share with stale/empty data on the first paint.
        if (!takeOffSite) return;
        // Some browsers' Web Share fallbacks concatenate `text` + `url` into
        // a single clipboard string (producing garbled output like
        // "<url><title>"). Pass URL only — the URL itself opens the site,
        // and any sharing surface that needs a label will derive it from the
        // page's <title>. Native iOS/Android share sheets handle this fine.
        const shareData = { url: window.location.href };
        if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
            try { await navigator.share(shareData); } catch (_) {}
            return;
        }
        // Fallback: copy URL to clipboard.
        try { await navigator.clipboard.writeText(window.location.href); } catch (_) {}
    };

    const setDashboardActions = useSetDashboardActions();
    // Keep handler closures in refs so the registration effect can stay
    // mount-only. Without refs, re-registering on every state change
    // briefly nulls the handlers — which (now that the nav buttons are
    // conditionally rendered) makes them flicker / disappear and feels
    // like a UI freeze.
    const getDataRef = useRef(getData);
    const handleShareRef = useRef(handleShare);
    useEffect(() => { getDataRef.current = getData; });
    useEffect(() => { handleShareRef.current = handleShare; });
    useEffect(() => {
        setDashboardActions({
            onRefresh: () => getDataRef.current(true),
            onShare: () => handleShareRef.current(),
        });
        return () => setDashboardActions({ onRefresh: null, onShare: null, isRefreshing: false });
    }, [setDashboardActions]);
    // Push isRefreshing changes through without touching the handlers.
    useEffect(() => {
        setDashboardActions({ isRefreshing: refreshing });
    }, [setDashboardActions, refreshing]);

    // Derive every weather card's daily min/max/min-time/max-time + sparkline
    // in a SINGLE pass over allData, memoized on allData. Previously each of
    // the ~12 WeatherDataBox instances recomputed these inline in JSX via
    // multiple filter()+sort() passes over the full history (5500+ rows) on
    // EVERY render — so a `now`-only update (60s poll) or a refresh re-ran
    // ~70 full-array passes synchronously, blocking the main thread for
    // hundreds of ms (the "freeze"). allData only changes on a full fetch,
    // so this memo is a cache-hit on `now`-only updates → near-zero cost.
    const dailyStats = useMemo(() => {
        if (!allData || !allData.length) return null;
        const startOfDay = dayjs().startOf('day');
        const todays = allData.filter(d => dayjs(d.SEND_TIME).isAfter(startOfDay));
        const last12 = [...allData]
            .sort((a, b) => new Date(a.SEND_TIME) - new Date(b.SEND_TIME))
            .slice(-12);

        // One linear scan for min/max + the timestamp at which each occurs.
        // Strict < / > keeps the FIRST occurrence among ties, matching the
        // previous stable-sort `[0]` behaviour exactly.
        const minMax = (getter) => {
            let min = Infinity, max = -Infinity, minT = null, maxT = null;
            for (const d of todays) {
                const raw = getter(d);
                if (raw == null) continue;
                const v = Number(raw);
                if (Number.isNaN(v)) continue;
                if (v < min) { min = v; minT = d.SEND_TIME; }
                if (v > max) { max = v; maxT = d.SEND_TIME; }
            }
            return {
                min: min === Infinity ? undefined : min,
                max: max === -Infinity ? undefined : max,
                minTime: minT ? dayjs(minT).format('HH:mm') : undefined,
                maxTime: maxT ? dayjs(maxT).format('HH:mm') : undefined,
            };
        };

        const temp = (d) => d.TEMPERATURE || d.TEMP;
        return {
            temp:     { ...minMax(temp),            spark: last12.map(temp) },
            pressure: { ...minMax(d => d.PRESSURE), spark: last12.map(d => d.PRESSURE) },
            moist:    { ...minMax(d => d.MOIST),    spark: last12.map(d => d.MOIST) },
            cloud:    { ...minMax(d => d.CL_BASE),  spark: last12.map(d => d.CL_BASE || d.CLOUD) },
            dewpoint: { ...minMax(d => d.D_POINT),  spark: last12.map(d => d.D_POINT || d.dewp) },
            sun:      { spark: last12.map(d => d.SUN) },
        };
    }, [allData]);

    if (loading) {
        return <StationDashboardSkeleton />;
    }

    // Check if station is offline
    const flightStatus = getFlightSafetyStatus(takeOffSite, now);
    const isOffline = flightStatus.isOffline;

    return (<>
        <Helmet>
            <title>{t(takeOffSite?.name)} | {t('Vazduhoplovni savez Srbije | Meteo')}</title>
            <meta
                name="description"
                content={`${t(takeOffSite?.name)} | ${t('Vazduhoplovni savez Srbije | Meteo')}`}
            />
            <meta name="keywords"
                  content={`VSS, Vazduhoplovni savez Srbije, Vazduhoplovni Savez Srbije, Meteo, ${takeOffSite?.name}`}/>
            <meta property="og:url" content={`https://meteo.vss.rs/site/${takeOffSite?.slug}`}/>
            <meta property="og:image" content={`/assets/images/${takeOffSite?.image}`}/>
            <meta property="twitter:image" content={`/assets/images/${takeOffSite?.image}`}/>
        </Helmet>
        <div className="bg-zinc-100 dark:bg-slate-900 min-h-screen">
            <StationDashboardHeader
                takeOffSite={takeOffSite}
                stationData={stationData}
                now={now}
                onRefresh={() => getData(true)}
                isRefreshing={refreshing}
                lastUpdate={lastUpdate}
            />

            {/* Offline Banner */}
            {isOffline && (
                <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 px-4 py-3">
                    <div className="flex items-center justify-center space-x-2 text-red-800 dark:text-red-200">
                        <FiWifiOff className="w-5 h-5" />
                        <span className="font-medium">{t('Weather station is not available now')}</span>
                        <span className="text-sm opacity-75">• {t('Showing last known data')}</span>
                    </div>
                </div>
            )}

            {/* Desktop Layout */}
            <div className="hidden md:flex p-2 mt-4 flex-col md:flex-row flex-wrap">
                {/* Map column. `my-2 sm:p-2` mirrors the cards' outer `my-2` +
                    inner `sm:m-2` chrome offset so the visible map edge aligns
                    with the visible card edges (top + bottom + sides). The map
                    stretches to fill the full cards-stack height via flex. */}
                <div className="w-full md:w-1/3 lg:w-1/4 my-2 sm:p-2 relative">
                    <div className="relative isolate overflow-hidden rounded-xl ring-1 ring-slate-900/10 shadow-md h-full">
                        <TakeoffPositionMap station={stationData} now={now} takeOffSite={takeOffSite}/>
                        {/* Forecast button overlay (top-right). Replaces the
                            former Point + Skew-T toolbar — single entry-point
                            now that /forecast hosts both views. */}
                        <button
                            type="button"
                            onClick={goToPointForecast}
                            className="absolute top-3 right-3 z-[400] flex items-center gap-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-md px-3 py-2 text-sm font-medium ring-1 ring-slate-900/10 shadow hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                            <FiBarChart2 className="w-4 h-4" />
                            {t('Forecast')}
                        </button>
                    </div>
                </div>
                {/* `xl:px-2 xl:pb-2` (no top) keeps the cards-container's
                    horizontal + bottom inset on wide screens while letting the
                    first card row's top align with the map column's top. */}
                <div className="flex p-0 xl:px-2 xl:pb-2 flex-wrap flex-1 items-stretch">
                    <WindBoxV2 now={now} trend={trend} all={allData} intervals={{interval15, interval30, interval60}}/>
                    <WeatherDataBox
                        title={t('Temperatura')}
                        value={now?.TEMPERATURE}
                        unit="°C"
                        icon={FiThermometer}
                        color="orange"
                        trend={trend?.diff_Temp_hour}
                        trendValue={trend?.diff_Temp_hour}
                        min={dailyStats?.temp.min}
                        max={dailyStats?.temp.max}
                        minTime={dailyStats?.temp.minTime}
                        maxTime={dailyStats?.temp.maxTime}
                        sparklineData={dailyStats?.temp.spark ?? null}
                    />
                    <WeatherDataBox
                        title={t('Vazdušni pritisak')}
                        value={now?.PRESSURE}
                        unit={t('hPa')}
                        icon={WiBarometer}
                        color="purple"
                        trend={trend?.diff_Pressure_hour}
                        trendValue={trend?.diff_Pressure_hour}
                        min={dailyStats?.pressure.min}
                        max={dailyStats?.pressure.max}
                        minTime={dailyStats?.pressure.minTime}
                        maxTime={dailyStats?.pressure.maxTime}
                        sparklineData={dailyStats?.pressure.spark ?? null}
                    />
                    <WeatherDataBox
                        title={t('Rel. vlažnost')}
                        value={now?.MOIST}
                        unit="%"
                        icon={FiDroplet}
                        color="blue"
                        trend={trend?.diff_MOIST_hour}
                        trendValue={trend?.diff_MOIST_hour}
                        min={dailyStats?.moist.min}
                        max={dailyStats?.moist.max}
                        minTime={dailyStats?.moist.minTime}
                        maxTime={dailyStats?.moist.maxTime}
                        sparklineData={dailyStats?.moist.spark ?? null}
                    />
                    <WeatherDataBox
                        title={t('Baza oblaka')}
                        value={now?.CLOUD}
                        unit="m"
                        icon={FiCloud}
                        color="gray"
                        trend={trend?.diff_CLOUD_hour}
                        trendValue={trend?.diff_CLOUD_hour}
                        min={dailyStats?.cloud.min}
                        max={dailyStats?.cloud.max}
                        minTime={dailyStats?.cloud.minTime}
                        maxTime={dailyStats?.cloud.maxTime}
                        sparklineData={dailyStats?.cloud.spark ?? null}
                    />
                    <WeatherDataBox
                        title={t('Tačka rosišta')}
                        value={now?.dewp}
                        unit="°C"
                        icon={FiDroplet}
                        color="blue"
                        trend={trend?.diff_D_POINT_hour}
                        trendValue={trend?.diff_D_POINT_hour}
                        min={dailyStats?.dewpoint.min}
                        max={dailyStats?.dewpoint.max}
                        minTime={dailyStats?.dewpoint.minTime}
                        maxTime={dailyStats?.dewpoint.maxTime}
                        sparklineData={dailyStats?.dewpoint.spark ?? null}
                    />
                    <WeatherDataBox
                        title={t('Osvetljenje')}
                        value={now?.SUN}
                        unit="W/m²"
                        icon={FiSunrise}
                        color="orange"
                        trend={trend?.diff_HEAT_INDEX_hour}
                        trendValue={trend?.diff_HEAT_INDEX_hour}
                        sparklineData={dailyStats?.sun.spark ?? null}
                    />
                </div>
            </div>
            <div className="hidden md:flex flex-wrap relative p-2">
                <Box sizes="w-full xl:w-1/3" uppercase title={t('Smer i snaga vetra')}
                     subtitle={`(${t('30 min avg')})`}>
                    <WindRoseChart/>
                </Box>
                <Box sizes="w-full xl:w-2/3" uppercase title={t('Brzina vetra, Max gust & Temperatura')}
                     subtitle={`${t('Interval 8h')} (${t(isMobile ? '30 min avg' : '15 min avg')})`}>
                    <WindBarbChart/>
                </Box>
            </div>

            {/* Mobile Layout */}
            <MobileTabLayout
                sections={[
                    // Overview Tab
                    <div className="p-2">
                        <div className="flex flex-wrap">
                            <WindBoxV2 now={now} trend={trend} all={allData} intervals={{interval15, interval30, interval60}}/>
                            <WeatherDataBox
                                title={t('Temperatura')}
                                value={now?.TEMPERATURE}
                                unit="°C"
                                icon={FiThermometer}
                                color="orange"
                                trend={trend?.diff_Temp_hour}
                                trendValue={trend?.diff_Temp_hour}
                                min={dailyStats?.temp.min}
                                max={dailyStats?.temp.max}
                                minTime={dailyStats?.temp.minTime}
                                maxTime={dailyStats?.temp.maxTime}
                                sparklineData={dailyStats?.temp.spark ?? null}
                            />
                            <WeatherDataBox
                                title={t('Vazdušni pritisak')}
                                value={now?.PRESSURE}
                                unit={t('hPa')}
                                icon={WiBarometer}
                                color="purple"
                                trend={trend?.diff_Pressure_hour}
                                trendValue={trend?.diff_Pressure_hour}
                                min={dailyStats?.pressure.min}
                                max={dailyStats?.pressure.max}
                                minTime={dailyStats?.pressure.minTime}
                                maxTime={dailyStats?.pressure.maxTime}
                                sparklineData={dailyStats?.pressure.spark ?? null}
                            />
                            <WeatherDataBox
                                title={t('Rel. vlažnost')}
                                value={now?.MOIST}
                                unit="%"
                                icon={FiDroplet}
                                color="blue"
                                trend={trend?.diff_MOIST_hour}
                                trendValue={trend?.diff_MOIST_hour}
                                min={dailyStats?.moist.min}
                                max={dailyStats?.moist.max}
                                minTime={dailyStats?.moist.minTime}
                                maxTime={dailyStats?.moist.maxTime}
                                sparklineData={dailyStats?.moist.spark ?? null}
                            />
                        </div>
                    </div>,
                    // Details Tab
                    <div className="p-2">
                        <div className="flex flex-wrap">
                            <WeatherDataBox
                                title={t('Baza oblaka')}
                                value={now?.CLOUD}
                                unit="m"
                                icon={FiCloud}
                                color="gray"
                                trend={trend?.diff_CLOUD_hour}
                                trendValue={trend?.diff_CLOUD_hour}
                                min={dailyStats?.cloud.min}
                                max={dailyStats?.cloud.max}
                                minTime={dailyStats?.cloud.minTime}
                                maxTime={dailyStats?.cloud.maxTime}
                                sparklineData={dailyStats?.cloud.spark ?? null}
                            />
                            <WeatherDataBox
                                title={t('Tačka rosišta')}
                                value={now?.dewp}
                                unit="°C"
                                icon={FiDroplet}
                                color="blue"
                                trend={trend?.diff_D_POINT_hour}
                                trendValue={trend?.diff_D_POINT_hour}
                                min={dailyStats?.dewpoint.min}
                                max={dailyStats?.dewpoint.max}
                                minTime={dailyStats?.dewpoint.minTime}
                                maxTime={dailyStats?.dewpoint.maxTime}
                                sparklineData={dailyStats?.dewpoint.spark ?? null}
                            />
                            <WeatherDataBox
                                title={t('Osvetljenje')}
                                value={now?.SUN}
                                unit="W/m²"
                                icon={FiSunrise}
                                subValue={`${t('Trend (1h)')}: ${trend?.diff_HEAT_INDEX_hour || 0} W/m²`}
                                color="orange"
                                sparklineData={dailyStats?.sun.spark ?? null}
                            />
                        </div>
                    </div>,
                    // Charts Tab
                    <div className="p-2">
                        <Box sizes="w-full" uppercase title={t('Smer i snaga vetra')}
                             subtitle={`(${t('30 min avg')})`}>
                            <WindRoseChart/>
                        </Box>
                        <Box sizes="w-full mt-4" uppercase title={t('Brzina vetra, Max gust & Temperatura')}
                             subtitle={`${t('Interval 8h')} (${t('30 min avg')})`}>
                            <WindBarbChart/>
                        </Box>
                    </div>,
                    // Map Tab. No Forecast button overlay here — the mobile
                    // floating Forecast button (below) covers this on every tab.
                    <div className="p-2">
                        <div className="relative isolate overflow-hidden rounded-xl ring-1 ring-slate-900/10 shadow-md h-[60vh]">
                            <TakeoffPositionMap station={stationData} now={now} takeOffSite={takeOffSite}/>
                        </div>
                    </div>
                ]}
            />

            {/* Mobile floating Forecast button. The per-tab Forecast entry
                points used to bury this action inside the Map tab; this FAB
                keeps it one tap away from every tab. Mobile-only (md:hidden) —
                desktop has the map-overlay button. z-[500] floats above page
                content + Leaflet (z-400) but stays below the nav/search
                overlays (z-[1100]+). */}
            <button
                type="button"
                onClick={goToPointForecast}
                aria-label={t('Forecast')}
                className="md:hidden fixed bottom-5 right-5 z-[500] flex items-center gap-2 bg-blue-600 text-white rounded-full pl-4 pr-5 py-3 text-sm font-semibold shadow-lg shadow-blue-600/30 ring-1 ring-blue-700/40 active:scale-95 transition-transform"
            >
                <WiDaySunnyOvercast className="w-6 h-6" />
                {t('Forecast')}
            </button>
        </div>
    </>);
};

export default StationDashboard;
